import { TourActions } from 'aria-viewer/tour'
import {
    PANORAMA_TAP,
    PANORAMA_DRAG_START,
    PANORAMA_DRAG_END,
    DEVICEMOTION_START,
    SET_PANORAMA_BLURRED,
    REGISTER_PANO_PLAYER,
    enableAccelerometerControls,
    setPanoramaUIVisible,
    setPanoramaIsBlurred,
    setPanoramaSize,
    resetDeviceOrientationAlphaAngle,
} from './panoplayer/actions/panoramaActions'
import {
    REGISTER_VIDEO_PLAYER,
    enableAccelerometerControls as enableVideoAccelerometerControls,
} from './videoplayer/actions/videoActions'
import { ORIENTATION_CHANGE_END } from 'aria-viewer/device/deviceActions'
import { distinctUntilChanged } from 'rxjs/operators'
import { debounceTime } from 'rxjs/operators'
import 'rxjs/add/operator/merge'
import 'rxjs/add/operator/takeWhile'
import 'rxjs/add/operator/delay'
import { Observable } from 'rxjs/Rx'
import { MEDIA_LOADING_STATUS } from 'aria-viewer/tour/constants'
import { MEDIA_TYPES } from 'aria-viewer/tour/constants/index'
import getBool from 'aria-viewer/utils/getBool'
import { getEnv } from 'aria-viewer/utils/environment'
import { isReduxBatch } from 'aria-viewer/utils/epicsCommon'
import { PAYMENT_MOUSE_ENTERED, PAYMENT_MOUSE_EXITED } from 'aria-viewer/payments/paymentActions'
import { isModalOpen, isModalClosed } from 'aria-viewer/utils/epicsCommon'

const isPaymentsMouseEntered = action =>
    isReduxBatch(action) && action.payload.some(element => element.type == PAYMENT_MOUSE_ENTERED)

const isPaymentsMouseExited = action =>
    isReduxBatch(action) && action.payload.some(element => element.type == PAYMENT_MOUSE_EXITED)

export default (action$, store) => {
    const isHideMainUIOnEngageEnabled = store.getState().getIn(['device', 'isHideMainUIOnEngageEnabled'])
    const isBlurMainMediaEnabled = getBool(getEnv('REACT_APP_BLUR_MAIN_MEDIA'))

    // Reset ui visible to true on load
    const panoLoading$ = action$
        .takeWhile(_ => isHideMainUIOnEngageEnabled)
        .ofType(TourActions.MAIN_MEDIA_LOADING_STATUS)
        .filter(
            action =>
                action.loadingStatus == MEDIA_LOADING_STATUS.loading &&
                store.getState().getIn(['tour', 'mainMedia', 'mediaType']) == MEDIA_TYPES.panorama
        )
        .distinctUntilChanged()
        .mapTo(setPanoramaUIVisible(true))

    // on pano tap, toggle ui visible only when user is not dragging
    const panoTap$ = action$
        .takeWhile(_ => isHideMainUIOnEngageEnabled)
        .ofType(PANORAMA_TAP)
        .debounceTime(250)
        .switchMap(action =>
            Observable.of(action)
                .filter(_ => !store.getState().getIn(['panorama', 'isUserDraggingPano']))
                .mapTo(setPanoramaUIVisible(!store.getState().getIn(['panorama', 'isPanoUIVisible'])))
        )

    // On drag start, hide ui is visible
    const panoDragStart$ = action$
        .takeWhile(_ => isHideMainUIOnEngageEnabled && store.getState().getIn(['device', 'isDeviceMobile']) == null)
        .ofType(PANORAMA_DRAG_START)
        .switchMap(action =>
            Observable.of(action)
                //.filter(_ => store.getState().getIn(['panorama', 'isPanoUIVisible']))
                .mapTo(setPanoramaUIVisible(false))
        )

    // Have ui visible after 2.5 seconds of drag ended
    const panoDragEnd$ = action$
        .takeWhile(_ => isHideMainUIOnEngageEnabled && store.getState().getIn(['device', 'isDeviceMobile']) == null)
        .ofType(PANORAMA_DRAG_END, PANORAMA_TAP)
        .debounceTime(2500)
        .switchMap(action =>
            Observable.of(action)
                .filter(
                    _ =>
                        !store.getState().getIn(['panorama', 'isUserDraggingPano']) &&
                        !store.getState().getIn(['panorama', 'isPanoUIVisible'])
                )
                .mapTo(setPanoramaUIVisible(true))
        )

    // Hide ui visible when device is in motion
    const panoDeviceMotionStart$ = action$
        .takeWhile(_ => isHideMainUIOnEngageEnabled && store.getState().getIn(['device', 'isDeviceMobile']) != null)
        .ofType(DEVICEMOTION_START)
        .switchMap(action => Observable.of(action).mapTo(setPanoramaUIVisible(false)))

    const panoramaAccelerometercontrolOnBlur$ = action$
        .ofType(SET_PANORAMA_BLURRED)
        // Toggle accelerometer control only in mobile
        .takeWhile(_ => store.getState().getIn(['device', 'isDeviceMobile']) != null)
        .delay(250)
        .map(action => enableAccelerometerControls(!action.isPanoramaBlurred))

    const panoramaOutOfFocus$ = action$
        .filter(action => isModalOpen(action) || isPaymentsMouseEntered(action))
        .takeWhile(_ => isBlurMainMediaEnabled)
        .mapTo(setPanoramaIsBlurred({ isPanoramaBlurred: true }))

    const panoramaFocused$ = action$
        .filter(action => isModalClosed(action) || isPaymentsMouseExited(action))
        .takeWhile(_ => isBlurMainMediaEnabled)
        .mapTo(setPanoramaIsBlurred({ isPanoramaBlurred: false }))

    const panoramaOrientationChanged$ = action$
        .ofType(ORIENTATION_CHANGE_END)
        .filter(_ => store.getState().getIn(['tour', 'mainMedia', 'mediaType']) == MEDIA_TYPES.panorama)
        .distinctUntilChanged()
        .map(({ viewportWidth, viewportHeight }) => setPanoramaSize(viewportWidth, viewportHeight))

    const panormaResetCameraDirection$ = action$
        .takeWhile(_ => store.getState().getIn(['device', 'isDeviceMobile']) != null)
        .ofType(REGISTER_PANO_PLAYER)
        .delay(200)
        .mapTo(resetDeviceOrientationAlphaAngle())

    const videoPlayerDisableAccelerometerControls$ = action$
        .ofType(REGISTER_VIDEO_PLAYER)
        .distinctUntilChanged()
        .filter(action => {
            const isDeviceIOS = store.getState().getIn(['device', 'isDeviceIOS'])
            const isGyroscopeEnabled = store.getState().getIn(['device', 'isGyroscopeEnabled'])
            const { player } = action
            const is360 = player.playback_type == 360

            return isDeviceIOS && !isGyroscopeEnabled && is360
        })
        .mapTo(enableVideoAccelerometerControls(false))

    return Observable.merge(
        panoLoading$,
        panoTap$,
        panoDragStart$,
        //panoDragEnd$,
        // panoDeviceMotionStart$,
        panoramaAccelerometercontrolOnBlur$,
        panoramaOutOfFocus$,
        panoramaFocused$,
        panoramaOrientationChanged$,
        panormaResetCameraDirection$,
        videoPlayerDisableAccelerometerControls$
    )
}
